import React, { useEffect, useRef } from 'react';
import { Animated, Platform, View } from 'react-native';
import { APITypes } from '../../../../../../../primary-record-types';
import { colors } from '../../../../../../../utils/theme';
import Stack from '../../../../../../v2BaseComponents/Stack';
import PressableDynamicOpacity from '../../../../../../v2BaseComponents/PressableDynamicOpacity';
import { getIntl } from '../../../../../../../utils/intl';
import Text from '../../../../../../v2BaseComponents/Text';
import getDateString from '../../../../../../../utils/getDateString';
import Button from '../../../../../../v2BaseComponents/Button';
import Icon from '../../../../../../v2BaseComponents/Icon';
import { PRScrollView } from '../../../../../../shared/Scroll';

const intl = getIntl('DesktopChatList', {
    defaultTitle: 'Chat on {date}',
    previousChats: 'Previous',
    addChat: 'New',
});

export type DesktopChatListOwnProps = {
    chats: APITypes.chatService.ListMedicalProfileChatsResponse;
    selectedChatId: string | null;
    onSelectChat: (chatId: string | null) => void;
    onDeleteChat: (chatId: string) => void;
};

export type DesktopChatListProps = DesktopChatListOwnProps;

const LIST_WIDTH = 240;

const ChatListItem: React.FC<{
    chat: APITypes.chatService.MedicalProfileChatListItem;
    onSelect: () => void;
    isSelected: boolean;
    index: number;
}> = ({ chat, onSelect, isSelected, index }) => {
    const hasTitle = chat.title && chat.title.length > 0;
    const title = hasTitle ? chat.title : intl('defaultTitle', { date: getDateString(chat.createdDateTime, 'date') });

    return (
        <PressableDynamicOpacity onPress={onSelect} testID={`chat-list-item-${index}`}>
            <Stack vertical align="flex-start" fullWidth padding={6}>
                <Text variant={isSelected ? 'bold-body-1' : 'body-1'} color={isSelected ? 'Dark-Text' : 'Gray-Text'}>
                    {title}
                </Text>
                {hasTitle ? (
                    <Text
                        variant={isSelected ? 'bold-caption-1' : 'caption-1'}
                        color={isSelected ? 'Dark-Text' : 'Gray-Text'}
                    >
                        {getDateString(chat.createdDateTime, 'date-time')}
                    </Text>
                ) : null}
            </Stack>
        </PressableDynamicOpacity>
    );
};

const DesktopChatList: React.FC<DesktopChatListProps> = ({ chats, selectedChatId, onSelectChat }) => {
    const entryAnimationRef = useRef<Animated.Value>(new Animated.Value(chats.items.length > 0 ? 1 : 0));
    useEffect(() => {
        Animated.timing(entryAnimationRef.current, {
            toValue: chats.items.length > 0 ? 1 : 0,
            duration: 200,
            useNativeDriver: Platform.OS === 'web',
        }).start();
    }, [chats.items.length]);

    return (
        <Animated.View
            style={{
                width: entryAnimationRef.current.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, LIST_WIDTH],
                }),
                opacity: entryAnimationRef.current.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 1],
                }),
                overflow: 'hidden',
                height: '100%',
                borderRightWidth: 1,
                borderRightColor: colors.Dividers,
            }}
        >
            <View style={{ width: LIST_WIDTH, height: '100%', flexDirection: 'column' }}>
                <Stack horizontal padding={6} justify="space-between" align="center">
                    <Stack horizontal space={2} align="center">
                        <Icon name="List" size="button-icon" color="Dark-Text" />
                        <Text variant="h6">{intl('previousChats')}</Text>
                    </Stack>
                    <Button
                        variant="ghost"
                        leftIcon="Plus"
                        onPress={() => onSelectChat(null)}
                        title={intl('addChat')}
                        testID="add-chat-button"
                    />
                </Stack>
                <PRScrollView style={{ width: '100%', flex: 1 }}>
                    <Stack vertical fullWidth>
                        {chats.items
                            .sort(
                                (a, b) => new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime(),
                            )
                            .map((chat, index) => (
                                <ChatListItem
                                    key={chat.chatId}
                                    chat={chat}
                                    isSelected={chat.chatId === selectedChatId}
                                    onSelect={() => onSelectChat(chat.chatId)}
                                    index={index}
                                />
                            ))}
                    </Stack>
                </PRScrollView>
            </View>
        </Animated.View>
    );
};

export default React.memo(DesktopChatList);
